import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "@material-ui/core/styles";
import useTiptapEditor from "./useTiptapEditor";
import Toolbar from "./Toolbar";
import { basicPreset } from "./presets";
import { countWords, resolveFeatures } from "./features";
import "./editor.css";

const DEFAULT_TOOLBAR =
  "undo redo | bold italic underline | alignleft aligncenter alignright | bullist numlist | removeformat";

/**
 * The one rich text editor for this app. Replaces @tinymce/tinymce-react.
 *
 *   <RichTextEditor
 *     value={html}
 *     onChange={setHtml}
 *     preset={documentPreset}
 *     toolbar="undo redo | bold italic underline | bullist numlist"
 *   />
 *
 * `toolbar` takes TinyMCE's pipe-delimited string format unchanged; see
 * toolbarItems.js for the names, and pass `customItems` for your own buttons.
 */
export default function RichTextEditor({
  value = "",
  onChange,
  preset, // () => Extension[] - see presets.js
  extensions, // or pass extensions directly; wins over `preset`
  toolbar = DEFAULT_TOOLBAR,
  customItems,
  editable = true,
  minHeight = 240,
  maxHeight,
  className = "",
  onReady, // (editor) => void - escape hatch to the raw Tiptap instance
  onLinkClick, // (href, event, editor) => void
  onSave, // (html) => void - Ctrl+S
  onPrint, // (html) => void - Ctrl+P; without it the browser prints the page
  // Opt-in features - see features.js. The editor itself handles wordCount;
  // the rest arrive composed into preset/toolbar/customItems/onSave.
  features,
}) {
  // Resolved once, like the extensions.
  const [{ wordCount }] = useState(() => resolveFeatures(features));

  // The extensions below are built once, so they cannot capture these props
  // directly - they would call the first render's handlers forever, with the
  // draft state as it was at mount. They read the latest through refs.
  const onSaveRef = useRef(onSave);
  const onPrintRef = useRef(onPrint);
  onSaveRef.current = onSave;
  onPrintRef.current = onPrint;

  // Resolved once. Extensions define the ProseMirror schema, which cannot
  // change after mount - remount with a different `key` to switch presets.
  const resolvedExtensions = useMemo(
    () => {
      const options = {
        onSave: (html) => {
          if (onSaveRef.current) onSaveRef.current(html);
        },
        // false hands Ctrl+P back to the browser - see WordShortcuts.js.
        onPrint: (html) => {
          if (!onPrintRef.current) return false;
          onPrintRef.current(html);
          return true;
        },
      };
      return extensions || (preset ? preset(options) : basicPreset(options));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleUpdate = useCallback(
    (html) => {
      if (onChange) onChange(html);
    },
    [onChange]
  );

  const handleCreate = useCallback(
    (instance) => {
      if (onReady) onReady(instance);
    },
    [onReady]
  );

  const theme = useTheme();

  const { hostRef, editor } = useTiptapEditor({
    extensions: resolvedExtensions,
    content: value,
    editable,
    onUpdate: handleUpdate,
    onCreate: handleCreate,
  });

  // Toolbar active/disabled states are derived from the editor, which is not
  // React state - so re-render whenever a transaction lands. Coalesced to one
  // render per frame: `transaction` fires on every keystroke, and an order
  // sheet is long enough for a render per character to be felt.
  const [, setTick] = useState(0);
  const frameRef = useRef(null);
  useEffect(() => {
    if (!editor) return undefined;
    const bump = () => {
      if (frameRef.current !== null) return;
      frameRef.current = window.requestAnimationFrame(() => {
        frameRef.current = null;
        setTick((tick) => tick + 1);
      });
    };
    editor.on("transaction", bump);
    return () => {
      editor.off("transaction", bump);
      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [editor]);

  // TinyMCE rendered into an iframe, so links there needed editor.on("click").
  // Tiptap renders inline, so this is an ordinary React handler - but for the
  // same reason a plain click must NOT navigate: it would unmount the SPA.
  // Link.openOnClick is false in the presets; this is the other half.
  const handleClick = (event) => {
    const anchor = event.target.closest && event.target.closest("a");
    if (!anchor) return;
    event.preventDefault();
    const href = anchor.getAttribute("href");
    if (onLinkClick) {
      onLinkClick(href, event, editor);
    } else if (href && (event.metaKey || event.ctrlKey)) {
      window.open(href, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div
      className={`rte ${className}`.trim()}
      style={{ fontFamily: theme.typography.fontFamily }}
    >
      {toolbar ? (
        <Toolbar editor={editor} toolbar={toolbar} customItems={customItems} />
      ) : null}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
      <div
        ref={hostRef}
        className="rte__content"
        style={{ minHeight, maxHeight, overflowY: maxHeight ? "auto" : undefined }}
        onClick={handleClick}
      />
      {wordCount ? (
        <div className="rte__footer">
          {countWords(value)} {countWords(value) === 1 ? "word" : "words"}
        </div>
      ) : null}
    </div>
  );
}
